import { DocumentTypeEnum } from 'api-hooks/order/travel-document';
import { OnChangeParams } from 'baseui/select';
import { NotePencilSVG } from 'common/assets';
import { DocumentSaleStatusEnum, EnumList } from 'common/constants/enum';
import stylesColors from 'common/styles/styles.colors';
import DeleteIconButton from 'components/common/delete-icon-button';
import Separator from 'components/common/separator';
import { Input } from 'components/elements';
import { FormState, useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import useDrawer from 'hooks/use-drawer';
import EnumSelectInput from 'legacy-modules/master/select-input/enum';
import {
  CardSection,
  FullContainer,
  HalfContainer,
  SectionContainer,
  SeparatorHorizontal,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import ReminderDrawer from './reminder-drawer';
import AddOnsTable from './tables/add-ons-table';
import DetailTable from './tables/detail-guest-table';
import GuestTable from './tables/guest-table';
import { addNewGuestDetail } from '../hooks/use-get-default-values';

interface Props {
  parentIndex: number;
  onRemove: () => void;
  fieldLength?: number;
}

export default function DocumentDetailField({
  parentIndex,
  onRemove,
  fieldLength,
}: Props) {
  const { t } = useTranslation();
  const drawer = useDrawer();
  const { editable } = useFormState();

  const methods = useFormContext();
  const { clearErrors, control, setValue, getValues, trigger } = methods;

  const documentTypeName = `documentSales.${parentIndex}.documentType` as const;
  const guestName = `documentSales.${parentIndex}.documentSaleGuests` as const;

  const [documentType, travelDocument] = useWatch({
    control,
    name: [documentTypeName, 'travelDocument'],
  });

  const documentSaleStatus = React.useMemo(() => {
    const isSubmitted =
      travelDocument?.documentSaleStatus === DocumentSaleStatusEnum.Submitted;
    const isFinished =
      travelDocument?.documentSaleStatus === DocumentSaleStatusEnum.Finished;
    const isProccessed =
      travelDocument?.documentSaleStatus === DocumentSaleStatusEnum.Processed;
    return { isSubmitted, isProccessed, isFinished };
  }, [travelDocument]);

  const onChangeDocumentType = React.useCallback(
    (val: OnChangeParams) => {
      const documentType = val.option?.id as DocumentTypeEnum;
      const guests = getValues(guestName).map((guest) => {
        return {
          ...guest,
          details: [addNewGuestDetail(documentType)],
        };
      });
      setValue(guestName, guests);
      trigger('documentSales');
    },
    [getValues, guestName, setValue, trigger],
  );

  const onCloseReminder = React.useCallback(
    (onClose?: () => void) => {
      if (!getValues(`documentSales.${parentIndex}.reminderDate`)) {
        setValue(`documentSales.${parentIndex}.reminder`, false);
      }
      onClose?.();
    },
    [getValues, parentIndex, setValue],
  );

  const onOpenSetReminder = React.useCallback(() => {
    drawer.showCustom({
      backdropClose: true,
      onBackdropClose: onCloseReminder,
      render(close) {
        return (
          <ReminderDrawer
            onClose={() => onCloseReminder(close)}
            parentIndex={parentIndex}
            parentMethods={methods}
          />
        );
      },
    });
  }, [drawer, methods, onCloseReminder, parentIndex]);

  const onChecked = React.useCallback(
    (val: boolean) => {
      clearErrors();
      if (val) {
        onOpenSetReminder();
      } else {
        setValue(`documentSales.${parentIndex}.reminderDate`, null, {
          shouldValidate: true,
        });
        setValue(`documentSales.${parentIndex}.reminderDescription`, null, {
          shouldValidate: true,
        });
      }
    },
    [clearErrors, onOpenSetReminder, parentIndex, setValue],
  );

  return (
    <CardSection>
      <FullContainer>
        <SectionContainer>
          <div style={{ flex: 1 }}>
            <Text variant="HeadingXSmall">
              {t('document:document_details')} {parentIndex + 1}
            </Text>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <Input
              type="check-box"
              name={`documentSales.${parentIndex}.reminder`}
              label={t('ticketSales:set_reminder')}
              noMargin
              onAfterChange={onChecked}
            />
            {editable && (
              <NotePencilSVG
                size={20}
                color={stylesColors.primaryDefault}
                onClick={onOpenSetReminder}
                cursor="pointer"
              />
            )}
          </div>
          <FormState>
            {({ editable }) =>
              (fieldLength || 0) > 1 &&
              editable && (
                <>
                  <SeparatorHorizontal $margin={16} />
                  <DeleteIconButton
                    onClick={onRemove}
                    rootStyle={{ height: '32px' }}
                  />
                </>
              )
            }
          </FormState>
        </SectionContainer>
      </FullContainer>
      <Separator gap={18} />

      <FullContainer>
        <SectionContainer>
          <HalfContainer>
            <EnumSelectInput
              enumType={EnumList.DocumentType}
              name={`documentSales.${parentIndex}.documentType`}
              label={t('document:document_type')}
              placeholder={t('document:document_type')}
              onAfterChange={onChangeDocumentType}
              required
              disabled={!!travelDocument}
              clearable={false}
            />
          </HalfContainer>
          <HalfContainer>
            <Input
              type="date-time"
              label={t('common:document_receive_date')}
              name={`documentSales.${parentIndex}.documentReceiveDate`}
            />
          </HalfContainer>
        </SectionContainer>
      </FullContainer>
      <Separator gap={18} />

      {/* {documentType === DocumentTypeEnum.visa && (
        <FullContainer>
          <HalfContainer>
            <VisaDocumentMultiInput
              name={`documentSales.${parentIndex}.visaDocuments`}
              onAfterSelect={onAfterSelect}
            />
          </HalfContainer>
        </FullContainer>
      )} */}
      {!!documentType && (
        <>
          <GuestTable
            parentIndex={parentIndex}
            documentSaleStatus={documentSaleStatus}
            documentType={documentType}
          />

          {methods.getValues(guestName)?.map((guest, index) => {
            return (
              <React.Fragment key={guest.customId}>
                <Separator gap={16} direction="vertical">
                  <Text variant="LabelMedium">{guest?.guestName}</Text>
                </Separator>
                {guest.guestName && (
                  <DetailTable
                    parentIndex={parentIndex}
                    documentSaleStatus={documentSaleStatus}
                    documentType={documentType}
                    guestIndex={index}
                  />
                )}
              </React.Fragment>
            );
          })}
          <Separator gap={24} />
          <AddOnsTable parentIndex={parentIndex} />
        </>
      )}
    </CardSection>
  );
}
